/**
 * dd-nudge-stack.js
 *
 * Scroll-position gate for the .dd-nudge-stack wrapper that hosts
 * <dd-review-nudge> + <dd-education-nudge>.
 *
 * Behavior:
 *   - At the top of the page (scrollY <= SCROLL_THRESHOLD_PX) the
 *     stack is hidden (opacity: 0, translateY 16px) so the nudges
 *     never pop over hero content on initial paint.
 *   - Once the user has scrolled past the threshold, the stack fades
 *     up. Individual nudge lifecycle animations (4-second delay,
 *     dismiss fade) keep working independently — they layer on top
 *     of the stack-level reveal. A nudge only renders to the user
 *     when both the stack is .is-scrolled AND the nudge has its own
 *     .is-shown class.
 *   - Preview mode (data-preview="true" on any child OR
 *     ?dd_rn_preview=1 / ?dd_nudge_force=1 in the URL) bypasses the
 *     scroll gate so the nudge can be inspected at the top of the
 *     page during testing.
 *   - On a re-fresh while already scrolled past the threshold the
 *     initial state is set correctly by the bootstrapping update()
 *     call before the listener attaches.
 *
 * Loaded inside {%- if customer -%} in layout/theme.liquid alongside
 * the other nudge assets, so this script never runs for guests.
 */
(function () {
  'use strict';

  if (window.__ddNudgeStackInit) return;
  window.__ddNudgeStackInit = true;

  // Threshold chosen empirically: ~one viewport-third of scroll on a
  // typical 800px tall mobile screen, or ~one hero header on desktop.
  // Anything less and the nudge can pop while the hero is still in view.
  var SCROLL_THRESHOLD_PX = 200;

  function isPreviewMode(stack) {
    try {
      var qs = window.location.search || '';
      if (/[?&]dd_rn_preview=1\b/.test(qs)) return true;
      if (/[?&]dd_nudge_force=1\b/.test(qs)) return true;
    } catch (_) { /* sandbox / opaque origin */ }
    if (!stack) return false;
    return !!stack.querySelector('[data-preview="true"]');
  }

  function init() {
    var stack = document.querySelector('.dd-nudge-stack');
    if (!stack) return;

    if (isPreviewMode(stack)) {
      stack.classList.add('is-scrolled');
      return;
    }

    var ticking = false;
    var lastState = false;

    function update() {
      ticking = false;
      var y = window.scrollY != null ? window.scrollY : (window.pageYOffset || 0);
      var scrolled = y > SCROLL_THRESHOLD_PX;
      if (scrolled === lastState) return;
      lastState = scrolled;
      stack.classList.toggle('is-scrolled', scrolled);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    // Bootstrap initial state before attaching the listener so a
    // hard-refresh while mid-scroll doesn't briefly show the closed
    // baseline before the first scroll event fires.
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
